import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { decompress } from 'lz-string';
import { BehaviorSubject, tap } from 'rxjs';
import {
	RallyanceReclamationNavigation,
	RallyanceReclamationReasons,
	RallyanceReclamationReclamatorPatchBody,
	RallyanceReclamationRekStatuses,
	RallyanceReclamationSQLResponse,
	RALLYANCE_RECLAMATION_ROUTES,
} from '../Interfaces/reclamations';
import { StoreService } from './store.service';

export const RALLYANCE_RECLAMATION_APP_PATH = 'reclamation-rallyance' as const;

type RallyanceReclamationRekStatusesDictionaryKeys = 'noStatus' | 'notApproved' | 'sentForClaim' | 'approved';

@Injectable({
	providedIn: 'root',
})
export class ReclamationService {
	constructor(private _ss: StoreService, private router: Router) {}

	private readonly appPath = RALLYANCE_RECLAMATION_APP_PATH;
	private http = inject(HttpClient);

	public readonly path = 'mobil/reclamation-rallyance';
	public readonly reasonsStorageName = 'RecReason';

	imageUploadingQueue$ = new BehaviorSubject<Array<string>>([]);
	uploadedImages$ = new BehaviorSubject<Array<string>>([]);

	allTasks$ = new BehaviorSubject<RallyanceReclamationSQLResponse[]>([]);
	selectedTask$ = new BehaviorSubject<RallyanceReclamationSQLResponse | null>(null);
	sugRecReasons$ = new BehaviorSubject<RallyanceReclamationReasons[]>([]);

	readonly rallyanceReclamationRekStatusesDictionary = {
		noStatus: '0',
		notApproved: '3',
		sentForClaim: '4',
		approved: '5',
		ApprovedAFTERSentForClaim: '6',
		notApprovedAFTERSentForClaim: '7',
	} as const;

	readonly rallyanceReclamationRekStatusesToSugStatuses = {
		[this.rallyanceReclamationRekStatusesDictionary.approved]: '1',
		[this.rallyanceReclamationRekStatusesDictionary.sentForClaim]: '2',
	} as const;

	readonly getSugStatusFromRekStatus = (rekStatus: typeof RallyanceReclamationRekStatuses[number]) => this.rallyanceReclamationRekStatusesToSugStatuses[rekStatus];

	getReclamationTasks() {
		return this._ss.get<RallyanceReclamationSQLResponse>(`${this.path}/for-reclamator`);
	}

	getReclamationReasons({ force }: { force?: boolean } = {}) {
		if (this.sugRecReasons$.value.length && !force) {
			return;
		}
		const reasons = localStorage.getItem('RecReason');

		if (!reasons) {
			console.log('no reasons');
			return;
		}

		const parsed: RallyanceReclamationReasons[] = JSON.parse(decompress(reasons));

		if (!parsed.length) {
			return;
		}

		this.sugRecReasons$.next(parsed);
	}

	init() {
		this.getReclamationReasons({ force: true });
	}

	navigateToReclamatorTasks = (whereTo: typeof RALLYANCE_RECLAMATION_ROUTES[number]) => this.router.navigate([this.appPath, whereTo]);

	resetTask = () => this.selectedTask$.next(null);
	selectNewTask = (task: RallyanceReclamationSQLResponse) => this.selectedTask$.next(task);

	getAllReclamationReasons() {
		return this._ss.get<RallyanceReclamationReasons>(`${this.path}/reasons`);
	}

	sendReclamationTaskAsReclamator(task: RallyanceReclamationReclamatorPatchBody, TeudaNum: string) {
		return this._ss.patch(`${this.path}/${TeudaNum}`, task);
	}

	resetState() {
		this.selectedTask$.next(null);
		this.allTasks$.next([]);
		this.resetImages();
	}

	uploadImageToServer(file: File) {
		if (!file) {
			throw new Error('no file');
		}
		const endpoint = `${this._ss.CustomerInformation.CustServer}/${this.path}/upload-camera-picture`;
		const formData = new FormData();
		formData.append('file', file);
		this.imageUploadingQueue$.next([...this.imageUploadingQueue$.value, file.name]);
		return this.http.post(`${endpoint}`, formData).pipe(
			tap(() => {
				const currentValue = this.imageUploadingQueue$.getValue();
				this.imageUploadingQueue$.next(currentValue.filter(name => name !== file.name));
			}),
		);
	}

	addImageToUploadedImages(imageName: string) {
		this.uploadedImages$.next([...this.uploadedImages$.value, imageName]);
	}

	resetImages() {
		this.uploadedImages$.next([]);
		this.imageUploadingQueue$.next([]);
	}
}
